import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, Download, FileText, MessageSquare, Send, Pencil, CalendarIcon, CalendarPlus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ClaimsAssistant } from "@/components/ClaimsAssistant";
import { useUserRoles } from "@/hooks/useUserRoles";

interface Claim {
  id: string;
  claim_number: string;
  insurance_carrier_name: string | null;
  loss_date: string;
  settlement_date: string | null;
  settlement_amount: number | null;
  claim_type: string;
  adjuster_name: string;
  adjuster_email: string;
  adjuster_phone: string;
  adjuster_company: string;
  adjuster_license: string;
  policyholder_name: string;
  policyholder_email: string;
  policyholder_phone: string;
  policyholder_address: string;
  policyholder_city: string;
  policyholder_state: string;
  policyholder_zip: string;
  family_dynamics: string | null;
  approved_terms: string | null;
  status: "pending" | "in_review" | "approved" | "denied";
  created_at: string;
  updated_at: string;
}

interface Attachment {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  created_at: string;
}

interface StatusHistory {
  id: string;
  old_status: string | null;
  new_status: string;
  created_at: string;
  notes: string | null;
  changed_by: string | null;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
}

interface ClaimNote {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  profiles?: {
    email: string;
  };
}

const ClaimDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [claim, setClaim] = useState<Claim | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [statusHistory, setStatusHistory] = useState<StatusHistory[]>([]);
  const [claimNotes, setClaimNotes] = useState<ClaimNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [extensionDate, setExtensionDate] = useState<Date | undefined>(undefined);
  const [savedExtensionDate, setSavedExtensionDate] = useState<string | null>(null);
  const [submittingExtension, setSubmittingExtension] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user && id) {
      fetchClaimDetails();
    }
  }, [user, id]);

  // Real-time subscription for new notes
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel('claim-notes-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'claim_notes',
          filter: `claim_id=eq.${id}`
        },
        async (payload) => {
          console.log('New note received:', payload);
          
          // Fetch profile for the new note
          const { data: profile } = await supabase
            .from("profiles")
            .select("email")
            .eq("id", payload.new.user_id)
            .single();
          
          const newNoteWithProfile = {
            ...payload.new,
            profiles: profile || { email: "Unknown" }
          };
          
          setClaimNotes((prev) => [...prev, newNoteWithProfile as ClaimNote]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  // Real-time subscription for claim status updates
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel('claim-status-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'claims',
          filter: `id=eq.${id}`
        },
        (payload) => {
          // Claim status updated via realtime
          
          // Update the claim state
          setClaim((prev) => prev ? { ...prev, ...payload.new } : null);
          
          // Show toast notification for status changes
          if (payload.old.status !== payload.new.status) {
            toast.info(`Claim status updated to ${payload.new.status.replace('_', ' ')}`);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  const fetchClaimDetails = async () => {
    try {
      const [claimResult, attachmentsResult, historyResult, notesResult] = await Promise.all([
        supabase.from("claims").select("*").eq("id", id).single(),
        supabase.from("claim_attachments").select("*").eq("claim_id", id).order("created_at", { ascending: false }),
        supabase.from("claim_status_history").select("*").eq("claim_id", id).order("created_at", { ascending: false }),
        supabase.from("claim_notes").select("*").eq("claim_id", id).order("created_at", { ascending: true })
      ]);

      if (claimResult.error) throw claimResult.error;
      if (!claimResult.data) {
        toast.error("Claim not found");
        navigate("/dashboard");
        return;
      }

      // Fetch profiles for notes
      const notesWithProfiles = await Promise.all(
        (notesResult.data || []).map(async (note) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("email")
            .eq("id", note.user_id)
            .single();
          
          return {
            ...note,
            profiles: profile || { email: "Unknown" }
          };
        })
      );

      // Fetch profiles for status history entries
      const historyWithProfiles = await Promise.all(
        (historyResult.data || []).map(async (h: any) => {
          if (!h.changed_by) return { ...h, profiles: null };
          const { data: profile } = await supabase
            .from("profiles")
            .select("first_name, last_name, email")
            .eq("id", h.changed_by)
            .maybeSingle();
          return { ...h, profiles: profile || null };
        })
      );

      setClaim(claimResult.data);
      setAttachments(attachmentsResult.data || []);
      setStatusHistory(historyWithProfiles);
      setClaimNotes(notesWithProfiles);
      const extDate = (claimResult.data as any)?.extension_approval_date;
      setSavedExtensionDate(extDate || null);
      setExtensionDate(extDate ? new Date(extDate + "T00:00:00") : undefined);
    } catch (error: any) {
      toast.error("Failed to load claim details");
      console.error("Error fetching claim:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const { data, error } = await supabase.storage
        .from('claim-attachments')
        .download(attachment.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error("Failed to download file");
      console.error("Error downloading file:", error);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim() || !user) return;

    setSubmittingNote(true);
    try {
      const { error } = await supabase.from("claim_notes").insert({
        claim_id: id,
        user_id: user.id,
        content: newNote.trim(),
      });

      if (error) throw error;

      toast.success("Note added successfully");
      setNewNote("");
      fetchClaimDetails();
    } catch (error: any) {
      toast.error("Failed to add note");
      console.error("Error adding note:", error);
    } finally {
      setSubmittingNote(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "approved":
        return "default";
      case "denied":
        return "destructive";
      case "in_review":
        return "secondary";
      default:
        return "outline";
    }
  };

  const { roles } = useUserRoles();
  const isAdjuster = roles.includes("adjuster") && !roles.includes("admin");

  // Compact spacing tokens for adjuster view
  const mainPadY = isAdjuster ? "py-4" : "py-8";
  const sectionMb = isAdjuster ? "mb-3" : "mb-6";
  const headerMb = isAdjuster ? "mb-3" : "mb-6";
  const gridGap = isAdjuster ? "gap-3" : "gap-6";
  const cardSpaceY = isAdjuster ? "space-y-2" : "space-y-4";
  const sectionMt = isAdjuster ? "mt-3" : "mt-6";

  const formatStatus = (status: string) => {
    if (isAdjuster) {
      if (status === "pending") return "Pending";
      if (status === "denied") return "Closed";
      // in_review, approved, etc.
      return "Active";
    }
    if (status === "approved") return "Active";
    return status.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!claim) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className={`flex-1 container mx-auto px-4 ${mainPadY} mt-20`}>
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className={sectionMb}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className={`flex items-center justify-between ${headerMb}`}>
          <div>
            <h1 className="text-4xl font-bold mb-2">Claim #{claim.claim_number}</h1>
            <p className="text-muted-foreground">
              Submitted on {format(new Date(claim.created_at), "MMMM d, yyyy")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/claims?edit=${claim.id}`, { state: { claimId: claim.id } })}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Badge variant={getStatusBadgeVariant(claim.status)} className="text-lg px-4 py-2">
              {formatStatus(claim.status)}
            </Badge>
          </div>
        </div>

        <div className={`grid ${gridGap} md:grid-cols-2 ${sectionMb}`}>
          <Card>
            <CardHeader>
              <CardTitle>Claim Information</CardTitle>
            </CardHeader>
            <CardContent className={cardSpaceY}>
              {claim.insurance_carrier_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Insurance Carrier</p>
                  <p className="font-medium">{claim.insurance_carrier_name}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Claim Type</p>
                <p className="font-medium">{claim.claim_type}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Loss Date</p>
                <p className="font-medium">{format(new Date(claim.loss_date), "MMMM d, yyyy")}</p>
              </div>
              {claim.settlement_date && (
                <div>
                  <p className="text-sm text-muted-foreground">Settlement Date</p>
                  <p className="font-medium">{format(new Date(claim.settlement_date), "MMMM d, yyyy")}</p>
                </div>
              )}
              {claim.settlement_amount && (
                <div>
                  <p className="text-sm text-muted-foreground">ALE Limit</p>
                  <p className="font-medium text-2xl">${claim.settlement_amount.toLocaleString()}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Policyholder Information</CardTitle>
            </CardHeader>
            <CardContent className={cardSpaceY}>
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{claim.policyholder_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{claim.policyholder_email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Phone</p>
                <p className="font-medium">{claim.policyholder_phone}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Address</p>
                <p className="font-medium">
                  {claim.policyholder_address}<br />
                  {claim.policyholder_city}, {claim.policyholder_state} {claim.policyholder_zip}
                </p>
              </div>
            </CardContent>
          </Card>

          {!isAdjuster && (
            <Card>
              <CardHeader>
                <CardTitle>Adjuster Information</CardTitle>
              </CardHeader>
              <CardContent className={cardSpaceY}>
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{claim.adjuster_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Company</p>
                  <p className="font-medium">{claim.adjuster_company}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{claim.adjuster_email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{claim.adjuster_phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">License #</p>
                  <p className="font-medium">{claim.adjuster_license}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {(claim.family_dynamics || claim.approved_terms) && (
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent className={cardSpaceY}>
                {claim.family_dynamics && (
                  <div>
                    <p className="text-sm text-muted-foreground">Family Dynamics</p>
                    <p className="font-medium">{claim.family_dynamics}</p>
                  </div>
                )}
                {claim.approved_terms && (
                  <div>
                    <p className="text-sm text-muted-foreground">Approved Terms</p>
                    <p className="font-medium">{claim.approved_terms}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className={`grid ${gridGap} md:grid-cols-2`}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Attachments</CardTitle>
                  <CardDescription>{attachments.length} file(s)</CardDescription>
                </div>
                <Button
                  onClick={() => navigate(`/claim/${id}/attachments`)}
                  size="sm"
                  variant="outline"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Manage Attachments
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {attachments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No attachments yet. Click "Manage Attachments" to upload documents.
                </p>
              ) : (
                <div className="space-y-3">
                  {attachments.slice(0, 3).map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{attachment.file_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatFileSize(attachment.file_size)} • {format(new Date(attachment.created_at), "MMM d, yyyy")}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(attachment)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {attachments.length > 3 && (
                    <p className="text-sm text-muted-foreground text-center pt-2">
                      And {attachments.length - 3} more... Click "Manage Attachments" to view all.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Status History</CardTitle>
              <CardDescription>{statusHistory.length} update(s)</CardDescription>
            </CardHeader>
            <CardContent>
              {statusHistory.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No status updates yet</p>
              ) : (
                <div className={cardSpaceY}>
                  {statusHistory.map((history, index) => {
                    const isFieldChange =
                      history.old_status === history.new_status &&
                      history.notes?.startsWith("Updated:");
                    return (
                      <div key={history.id}>
                        <div className="flex items-start space-x-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {isFieldChange ? (
                                <Badge variant="outline" className="text-xs">
                                  Claim Updated
                                </Badge>
                              ) : (
                                <>
                                  {history.old_status && (
                                    <>
                                      <Badge variant={getStatusBadgeVariant(history.old_status)} className="text-xs">
                                        {formatStatus(history.old_status)}
                                      </Badge>
                                      <span className="text-muted-foreground">→</span>
                                    </>
                                  )}
                                  <Badge variant={getStatusBadgeVariant(history.new_status)} className="text-xs">
                                    {formatStatus(history.new_status)}
                                  </Badge>
                                </>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(history.created_at), "MMM d, yyyy 'at' h:mm a")}
                              {(() => {
                                const fullName = [history.profiles?.first_name, history.profiles?.last_name]
                                  .filter(Boolean)
                                  .join(" ");
                                const who = fullName || history.profiles?.email;
                                return who ? <> · by <span className="font-medium text-foreground">{who}</span></> : null;
                              })()}
                            </p>
                            {history.notes && (
                              <p className="text-sm mt-1 break-words">{history.notes}</p>
                            )}
                          </div>
                        </div>
                        {index < statusHistory.length - 1 && <Separator className={isAdjuster ? "mt-2" : "mt-4"} />}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className={`grid ${gridGap} ${sectionMt} lg:grid-cols-3`}>
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <CardTitle>Notes & Comments</CardTitle>
              </div>
              <CardDescription>
                Communicate with TPS Housing about this claim
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={cardSpaceY}>
                {claimNotes.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No notes yet. Be the first to add a comment.
                  </p>
                ) : (
                  <div className={`${cardSpaceY} max-h-96 overflow-y-auto`}>
                    {claimNotes.map((note) => (
                      <div key={note.id} className={`border-l-2 border-primary/20 pl-4 ${isAdjuster ? "py-1" : "py-2"}`}>
                        <div className={`flex items-start justify-between ${isAdjuster ? "mb-1" : "mb-2"}`}>
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <MessageSquare className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {note.profiles?.email || "Unknown User"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(note.created_at), "MMM d, yyyy 'at' h:mm a")}
                              </p>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm ml-10">{note.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                <form onSubmit={handleAddNote} className="space-y-3 pt-4 border-t">
                  <Textarea
                    placeholder="Add a note or comment..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="min-h-[100px]"
                    disabled={submittingNote}
                  />
                  <div className="flex justify-end">
                    <Button type="submit" disabled={submittingNote || !newNote.trim()}>
                      {submittingNote ? (
                        <>Posting...</>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Claim Update
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CalendarPlus className="h-5 w-5" />
                <CardTitle>Extension Approvals</CardTitle>
              </div>
              <CardDescription>
                Select an approved extension date for this claim
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !extensionDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {extensionDate ? format(extensionDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={extensionDate}
                    onSelect={setExtensionDate}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <Button
                className="w-full"
                disabled={submittingExtension}
                onClick={async () => {
                  const newValue = extensionDate ? format(extensionDate, "yyyy-MM-dd") : null;
                  if (newValue === savedExtensionDate) {
                    toast.info("No changes to submit");
                    return;
                  }
                  setSubmittingExtension(true);
                  try {
                    const { error: updateError } = await supabase
                      .from("claims")
                      .update({ extension_approval_date: newValue } as any)
                      .eq("id", id!);
                    if (updateError) throw updateError;

                    const { error: fnError } = await supabase.functions.invoke(
                      "notify-extension-approval",
                      {
                        body: {
                          claimId: id,
                          extensionDate: newValue,
                          previousDate: savedExtensionDate,
                        },
                      }
                    );
                    if (fnError) console.error("Notification error:", fnError);

                    setSavedExtensionDate(newValue);
                    toast.success("Extension approval submitted");
                  } catch (err: any) {
                    console.error(err);
                    toast.error("Failed to submit extension approval");
                  } finally {
                    setSubmittingExtension(false);
                  }
                }}
              >
                {submittingExtension ? "Submitting..." : "Submit"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />

      {/* Claims Assistant Chatbot */}
      <ClaimsAssistant claimId={id} claimNumber={claim.claim_number} />
    </div>
  );
};

export default ClaimDetail;
